import { Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";

interface StarRatingProps {
  rating: number;
  maxRating?: number;
  size?: "sm" | "md" | "lg";
  interactive?: boolean;
  onRatingChange?: (rating: number) => void;
}

const sizeMap = {
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-7 h-7",
};

const StarRating = ({
  rating,
  maxRating = 5,
  size = "md",
  interactive = false,
  onRatingChange,
}: StarRatingProps) => {
  const stars = Array.from({ length: maxRating }, (_, i) => {
    const fillPercent = Math.min(1, Math.max(0, rating - i));
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!interactive) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const isLeftHalf = e.clientX - rect.left < rect.width / 2;
      onRatingChange?.(i + (isLeftHalf ? 0.5 : 1));
    };
    return (
      <div
        key={i}
        className={cn("relative", interactive && "cursor-pointer")}
        onClick={handleClick}
        role={interactive ? "button" : undefined}
        aria-label={interactive ? `Rate ${i + 1} stars` : undefined}
      >
        <Star className={cn(sizeMap[size], "fill-star-empty/30 text-star-empty")} />
        {fillPercent > 0 && (
          <div
            className="absolute inset-0 overflow-hidden pointer-events-none"
            style={{ width: `${fillPercent * 100}%` }}
          >
            <Star className={cn(sizeMap[size], "fill-star text-star")} />
          </div>
        )}
      </div>
    );
  });

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <div className="flex gap-0.5">{stars}</div>
        <span className="text-sm font-semibold text-foreground min-w-[2rem] text-center">
          {rating.toFixed(1)}
        </span>
      </div>
      {interactive && (
        <Slider
          min={0}
          max={maxRating * 10}
          step={1}
          value={[Math.round(rating * 10)]}
          onValueChange={([val]) => onRatingChange?.(val / 10)}
          className="w-full max-w-[200px]"
        />
      )}
    </div>
  );
};

export default StarRating;
